// client/src/components/pages/LexiStar/StatusBoard/format.js
//
// Number, duration and data-age formatting shared by the control-tower pages.
// Litres are the unit of record (D21); times render in SAST (MINING_TZ) via
// utils/dateUtils. Numbers group with a comma and use a decimal point
// ("34,440 kg", "4.2 km") as the LS-09 wireframes do — en-ZA's ICU data would
// give a decimal comma.

import { formatMiningTime, formatMiningDateTime } from '../../../../utils/dateUtils';

const isNum = (v) => v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v));

export const fmtNum = (v, digits = 0) =>
  isNum(v)
    ? Number(v).toLocaleString('en-GB', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      })
    : '—';

export const fmtL = (v, digits = 0) => (isNum(v) ? `${fmtNum(v, digits)} L` : '—');
export const fmtKm = (v, digits = 0) => (isNum(v) ? `${fmtNum(v, digits)} km` : '—');
export const fmtKg = (v) => (isNum(v) ? `${fmtNum(v)} kg` : '—');
export const fmtPct = (ratio, digits = 0) =>
  isNum(ratio) ? `${(Number(ratio) * 100).toFixed(digits)}%` : '—';

/** Signed litres: −7 L / +3 L (under budget is negative projected over/under). */
export const fmtSignedL = (v, digits = 0) => {
  if (!isNum(v)) return '—';
  const n = Number(v);
  const s = Math.abs(n).toLocaleString('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
  if (n > 0) return `+${s} L`;
  if (n < 0) return `−${s} L`;
  return '0 L';
};

export const fmtRand = (v) =>
  isNum(v) ? `${Number(v) < 0 ? '−' : ''}R${fmtNum(Math.abs(Number(v)))}` : '—';

/** 2h14 / 48m / 35s */
export function fmtDuration(seconds) {
  if (!isNum(seconds)) return '—';
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h${String(m % 60).padStart(2, '0')}`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export const secondsSince = (iso, now = Date.now()) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, (now - t) / 1000);
};

export const fmtTime = (iso) => formatMiningTime(iso) || '—';
export const fmtDateTime = (iso) => formatMiningDateTime(iso) || '—';

// ── Data age (LS-09 §3 "relative time with colour escalation") ─────────────
export const AGE_AGING_S = 120;
export const AGE_STALE_S = 900;

/**
 * @returns {{text:string, level:'fresh'|'aging'|'stale'|'unknown', className:string, title:string}}
 */
export function dataAge(iso, now = Date.now()) {
  const s = secondsSince(iso, now);
  if (s === null) {
    return {
      text: 'no data',
      level: 'unknown',
      className: 'text-slate-400',
      title: 'No position yet',
    };
  }
  let text;
  if (s < 60) text = `${Math.round(s)} s ago`;
  else if (s < 3600) text = `${Math.floor(s / 60)} min ago`;
  else if (s < 86400) text = `${Math.floor(s / 3600)} h ago`;
  else text = `${Math.floor(s / 86400)} d ago`;
  const level = s < AGE_AGING_S ? 'fresh' : s < AGE_STALE_S ? 'aging' : 'stale';
  const className =
    level === 'fresh'
      ? 'text-slate-500'
      : level === 'aging'
        ? 'text-amber-700'
        : 'text-red-700 font-semibold';
  return { text, level, className, title: fmtDateTime(iso) };
}
